import "./login.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { userRoles } from "../../components/Authentication/RouteGuards";
import { signIn } from "../../services/userService";
import { validateEmail } from "../../utils/helpers";
import {
  Alert,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";

const Login = () => {
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  function handleUserRedirection(user) {
    if (user.role === userRoles.superAdmin) {
      navigate("/admin");
    } else if (user.role === userRoles.admin) {
      navigate(`/admin/${user?.domain}`);
    } else {
      navigate("/login");
    }
  }

  // Client-side validation: both fields required, email must be well-formed.
  const validate = () => {
    let valid = true;

    if (!email.trim()) {
      setEmailError(t("requiredField"));
      valid = false;
    } else if (!validateEmail(email)) {
      setEmailError(t("invalidEmail"));
      valid = false;
    } else {
      setEmailError("");
    }

    if (!password) {
      setPasswordError(t("requiredField"));
      valid = false;
    } else {
      setPasswordError("");
    }

    return valid;
  };

  const signInUser = () => {
    setSubmitting(true);
    signIn({ email, password })
      .then((result) => {
        if (result?.user) {
          handleUserRedirection(result.user);
          return;
        }
        // 401 = wrong password, 402 = user not found. Show one generic
        // message for both (don't reveal which field was wrong).
        const isCredentialError =
          result?.status === 401 || result?.status === 402;
        setLoginError(
          isCredentialError
            ? t("invalidCredentials")
            : result?.message || t("loginFailed")
        );
      })
      .finally(() => setSubmitting(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoginError("");
    if (!validate()) return;
    signInUser();
  };

  return (
    <div className="login_wrapper">
      <form className="form_wrapper" onSubmit={handleSubmit} noValidate>
        {loginError && (
          <Alert severity="error" sx={{ marginBottom: "15px" }}>
            {loginError}
          </Alert>
        )}

        <FormControl sx={{ marginBottom: "15px" }}>
          <TextField
            variant="outlined"
            name="email"
            label={t("email")}
            placeholder={t("email")}
            autoFocus
            error={!!emailError}
            helperText={emailError}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
              if (loginError) setLoginError("");
            }}
          />
        </FormControl>

        <FormControl sx={{ marginBottom: "15px" }}>
          <TextField
            variant="outlined"
            name="password"
            label={t("password")}
            placeholder={t("password")}
            type={showPassword ? "text" : "password"}
            error={!!passwordError}
            helperText={passwordError}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (passwordError) setPasswordError("");
              if (loginError) setLoginError("");
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((show) => !show)}
                    edge="end"
                    type="button"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>

        <Button
          className="login_btn"
          variant="contained"
          color="info"
          type="submit"
          disabled={submitting}
        >
          {t("login")}
        </Button>
      </form>
    </div>
  );
};

export default Login;
